import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, startWith, Subject, take, takeUntil } from 'rxjs';

import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentMethodFeatureSelectors } from '@amfam/billing/payment-method/feature';
import {
  AchWithdrawal,
  CreditCard,
  CreditCardType,
  PaymentMethod
} from '@amfam/billing/payment-method/util';
import { PaymentMethodRadioOption } from '@amfam/shared/models';
import { routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import { OneTimePayActions, OneTimePayFeatureFacade } from '../../..';
import {
  AccountForSimplifiedPayment,
  OneTimePaySetup,
  PaymentOption,
  simplifiedPaymentReviewData
} from '../../+state/one-time-pay-feature.models';

@Component({
  selector: 'ds-hs-choose-account-wrapper',
  templateUrl: './hs-choose-account-wrapper.compnent.html',
  styleUrls: ['./hs-choose-account-wrapper.component.scss'],
  standalone: false
})
export class HSChooseAccountWrapperComponent implements OnInit, OnDestroy {
  @Output() addCreditDebitEvent = new EventEmitter<void>();
  @Output() addCheckingAccountEvent = new EventEmitter<void>();

  accountSelectionFormGroup: FormGroup;
  paymentMethodFormGroup: FormGroup;
  paymentDateValue = new Date();
  billAccountOptions$: Observable<AccountForSimplifiedPayment[]>;
  billAccountOptionFiltered: AccountForSimplifiedPayment[];
  paymentMethodOptions: PaymentMethodRadioOption[];
  defaultPaymentMethod: PaymentMethodRadioOption;
  reviewPaymentInfo: simplifiedPaymentReviewData;
  selectedAmount: number;
  secondaryButtonName: string;
  addPaymentMethodType: string;
  paymentMethods: PaymentMethod[];
  iframeTokenDetails$: Observable<PaymentMethod>;
  enableSubmitButton = false;

  tokenType: string;
  lastFour: string;
  creditCardType: CreditCardType;
  cardExpiry: string;
  token: string;
  cardType: string;
  checkingAccount: string;
  routingNumber: string;
  iframeToken: AchWithdrawal | CreditCard = null;
  regex = /\*/g;
  data: OneTimePaySetup;
  total$: Observable<{
    sum: number;
  }>;

  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private spinner: LoadingSpinnerService,
    private formBuilder: FormBuilder,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private copyService: CopyService
  ) {}
  get valid() {
    if (this.iframeToken) {
      return true;
    } else return this.accountSelectionFormGroup.invalid && this.paymentMethodFormGroup.invalid;
  }
  ngOnInit(): void {
    this.billAccountOptions$ =
      this.oneTimePayFacade.getSimplifiedPaymentBillAccountsWithAlertMessageList();

    this.getForm();

    this.secondaryButtonName = this.copyService.getCopy('shared.cancel');

    this.getPaymentMethods();

    this.total$ = this.handleCheckBoxInteraction();

    this.iframeTokenDetails$ = this.store.select(
      PaymentMethodFeatureSelectors.selectAddPaymentMethodTokenInfo
    );

    this.iframeTokenDetails$.pipe(takeUntil(this.stop$)).subscribe(val => {
      if (val) {
        this.iframeToken = val.achWithdrawal ? val.achWithdrawal : val.creditCard;
        this.tokenType = val.tokenType;
        if (val.tokenType === 'CC') {
          this.token = val.paymentAccountId;
          const tokenDetails = this.iframeToken as CreditCard;
          this.cardExpiry = tokenDetails.cardExpiry;
          this.cardType = tokenDetails.cardType;
          this.lastFour = tokenDetails.cardNumber.replace(this.regex, '');
        } else {
          this.token = val.paymentAccountId;
          const tokenDetails = this.iframeToken as AchWithdrawal;
          this.checkingAccount = tokenDetails.accountNumber.replace(this.regex, '');
          this.routingNumber = tokenDetails.routingNumber;
        }
      }
    });
  }

  gotoReview() {
    const regex = /[$,]/g;

    const paymentDateAmountPolicy = {
      paymentDate: new Date().toISOString(),
      totalAmount: this.accountSelectionFormGroup
        .get(this.billAccountOptionFiltered[0].id)
        .value.paymentOptions['amount'].replace(regex, ''),
      policyNumber: this.billAccountOptionFiltered[0].id
    };

    if (this.iframeToken === null) {
      const selectedPaymentMethod: PaymentMethodRadioOption =
        this.paymentMethodFormGroup.controls['paymentMethod'].value;

      this.reviewPaymentInfo = {
        ...paymentDateAmountPolicy,

        cardDetails: selectedPaymentMethod.bankIcon
          ? 'Checking ' + selectedPaymentMethod.lastFourOrThree
          : selectedPaymentMethod.icon + ' ' + selectedPaymentMethod.lastFourOrThree
      };
    } else {
      this.reviewPaymentInfo = {
        ...paymentDateAmountPolicy,
        cardDetails:
          this.tokenType === 'CC'
            ? this.cardType + '<br />' + this.lastFour
            : 'Checking <br />' + this.checkingAccount
      };

      this.oneTimePayFacade.registerBillAccountsBeforeOneTimePayPhoenix(this.reviewPaymentInfo);
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  cancel() {
    this.store.dispatch(OneTimePayActions.goToCancelPath());
  }

  getForm(): void {
    this.spinner.start();
    combineLatest([
      this.store.select(routerQuery.getRouterPreviousStateUrl),
      this.billAccountOptions$,
      this.oneTimePayFacade.getOTPEntities(),
      this.oneTimePayFacade.buildPaymentMethodOptions()
    ])
      .pipe(take(1))
      .subscribe(([previousUrl, billAccountOptions, otpEntities, paymentMethodOptions]) => {
        this.store.dispatch(OneTimePayActions.setOneTimePayCancelPath({ url: previousUrl }));

        this.paymentMethodOptions = paymentMethodOptions;
        this.billAccountOptionFiltered = billAccountOptions.filter(ba => ba.isSelected);
        const paymentOptions = this.billAccountOptionFiltered[0].paymentOptions.filter(
          paymentOption => paymentOption.text !== 'Other amount'
        );
        this.billAccountOptionFiltered[0].paymentOptions = paymentOptions;

        const formControls = billAccountOptions
          .filter(billAccountOption => billAccountOption.isSelected)
          .map(
            billAccountOption =>
              new FormControl({
                value: billAccountOption.isSelected,
                disabled: billAccountOption.isDisabled
              })
          );

        this.accountSelectionFormGroup = this.formBuilder.group(
          {
            option: new FormArray(formControls)
          },
          { validators: ValidationService.minimumCheckedCheckboxCheckedValidator() }
        );

        const key = Object.keys(otpEntities)[0];
        this.paymentMethodFormGroup = this.formBuilder.group({
          paymentMethod: [
            this.getDefaultPaymentMethod(
              paymentMethodOptions,
              otpEntities[key]?.selectedPaymentAccount
            ),
            Validators.compose([Validators.required])
          ]
        });

        billAccountOptions
          .filter(billAccountOption => billAccountOption.isSelected)
          .map(billAccountOption => {
            this.selectedAmount = +billAccountOption.paymentOptions[0].amount.replace('$', '');
            this.accountSelectionFormGroup.addControl(
              billAccountOption.id,
              this.formBuilder.group({
                paymentOptions: new FormControl({
                  value: billAccountOption.paymentOptions[0],
                  disabled: !billAccountOption.isSelected
                }),
                otherAmount: new FormControl()
              })
            );
          });

        this.spinner.stop();
      });
  }

  getDefaultPaymentMethod(
    paymentMethodOptions: PaymentMethodRadioOption[],
    paymentNickName: string
  ) {
    this.defaultPaymentMethod = paymentMethodOptions.find(
      paymentMethodOption => paymentMethodOption.nickName === paymentNickName
    );
    return this.defaultPaymentMethod ? this.defaultPaymentMethod : paymentMethodOptions[0];
  }

  addCheckingAccount(): void {
    this.addCheckingAccountEvent.emit();
  }

  addCreditDebit(): void {
    this.addCreditDebitEvent.emit();
  }

  handleCheckBoxInteraction() {
    return combineLatest([
      this.billAccountOptions$,
      this.accountSelectionFormGroup.valueChanges.pipe(
        startWith(this.accountSelectionFormGroup.value)
      ),
      this.paymentMethodFormGroup?.valueChanges.pipe(startWith(this.paymentMethodFormGroup?.value))
    ]).pipe(
      map(([billAccountOptions]) => {
        (<FormArray>this.accountSelectionFormGroup.get('option')).controls.forEach((option, i) => {
          billAccountOptions[i].isSelected = option.value;
        });
        let sum = 0;

        billAccountOptions = billAccountOptions.filter(option => option.isSelected);
        if (billAccountOptions.length) {
          billAccountOptions.forEach(billAccountOption => {
            const selectedPaymentOption: PaymentOption = this.accountSelectionFormGroup
              .get(billAccountOption.id)
              .get('paymentOptions').value;
            const selectedPaymentAmount = selectedPaymentOption.amount.replace(/\$|,/g, '');
            sum += Math.round(Number(selectedPaymentAmount) * 100) / 100;
          });
        }
        return { sum };
      })
    );
  }

  updateAccountEvent(data: {
    billAccountOptions: AccountForSimplifiedPayment[];
    billAccountOption: AccountForSimplifiedPayment;
  }) {
    this.billAccountOptions$ = this.oneTimePayFacade.getUpdatedSimplifiedPaymentBillAccounts(
      of(data.billAccountOptions),
      data.billAccountOption
    );
  }

  private getPaymentMethods(): void {
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethods)

      .pipe(takeUntil(this.stop$))
      .subscribe(paymentMethods => {
        this.paymentMethods = paymentMethods;
      });
  }
}
